import type { ExternalDeploymentEvidence } from '@lixionary-jira/domain';
import { Checkbox, Field, FormSection, Input, Textarea } from '../../ui/forms';

export const blankExternalEvidence = (): ExternalDeploymentEvidence => ({
  sourceRevision: '',
  deployedBy: '',
  deployedAt: '',
  environmentObservation: '',
  deploymentReference: '',
  log: '',
  evidenceRefs: [],
  checks: [],
});

export function ExternalEvidenceForm({
  value,
  checks,
  onChange,
}: {
  value?: ExternalDeploymentEvidence;
  checks: string[];
  onChange: (v: ExternalDeploymentEvidence) => void;
}) {
  const v = value ?? blankExternalEvidence();
  const set = (patch: Partial<ExternalDeploymentEvidence>) => onChange({ ...v, ...patch });
  const date = v.deployedAt ? new Date(v.deployedAt) : null;
  const localDate =
    date && Number.isFinite(date.getTime())
      ? new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
      : '';
  return (
    <FormSection
      title="Existing deployment evidence"
      description="An admin reviews what was deployed and the current environment state. Acceptance records delivery without running a deployment."
    >
      <Field label="Deployed commit SHA">
        {(p) => (
          <Input
            {...p}
            required
            value={v.sourceRevision}
            onChange={(e) => set({ sourceRevision: e.target.value })}
          />
        )}
      </Field>
      <Field label="Reported deployer">
        {(p) => (
          <Input {...p} required value={v.deployedBy} onChange={(e) => set({ deployedBy: e.target.value })} />
        )}
      </Field>
      <Field label="Deployment time" hint="Your local time zone.">
        {(p) => (
          <Input
            {...p}
            type="datetime-local"
            required
            value={localDate}
            onChange={(e) =>
              set({ deployedAt: e.target.value ? new Date(e.target.value).toISOString() : '' })
            }
          />
        )}
      </Field>
      <Field label="Reported artifact digest" optional>
        {(p) => (
          <Input
            {...p}
            value={v.artifactDigest ?? ''}
            onChange={(e) => set({ artifactDigest: e.target.value || null })}
          />
        )}
      </Field>
      <Field label="Current environment observation">
        {(p) => (
          <Textarea
            {...p}
            required
            value={v.environmentObservation}
            onChange={(e) => set({ environmentObservation: e.target.value })}
          />
        )}
      </Field>
      <Field label="Execution reference" hint="Provide an execution reference or log.">
        {(p) => (
          <Input
            {...p}
            value={v.deploymentReference ?? ''}
            onChange={(e) => set({ deploymentReference: e.target.value })}
          />
        )}
      </Field>
      <Field label="Deployment log" optional>
        {(p) => (
          <Textarea {...p} rows={4} value={v.log ?? ''} onChange={(e) => set({ log: e.target.value })} />
        )}
      </Field>
      {checks
        .filter((c) => c.trim())
        .map((name) => {
          const result = v.checks.find((c) => c.name === name) ?? { name, passed: false, observation: '' };
          const update = (patch: Partial<typeof result>) =>
            set({ checks: [...v.checks.filter((c) => c.name !== name), { ...result, ...patch }] });
          return (
            <div key={name} className="stack sm">
              <Checkbox
                label={`${name}: passed`}
                checked={result.passed}
                onChange={(passed) => update({ passed })}
              />
              <Field label={`${name}: observation`}>
                {(p) => (
                  <Textarea
                    {...p}
                    required
                    value={result.observation}
                    onChange={(e) => update({ observation: e.target.value })}
                  />
                )}
              </Field>
            </div>
          );
        })}
    </FormSection>
  );
}
